import { Link } from 'react-router-dom';
import { Check, Target, Shield, Lightbulb, Users, Play, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import CalendlyButton from '../components/CalendlyButton';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: "Expertise",
      description: "Une connaissance approfondie du droit immobilier et des mécanismes de division."
    },
    {
      icon: Target,
      title: "Précision",
      description: "Une approche méthodique et rigoureuse pour chaque projet."
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "Des solutions créatives adaptées à chaque situation."
    },
    {
      icon: Users,
      title: "Accompagnement",
      description: "Un suivi personnalisé tout au long de votre projet."
    }
  ];

  const expertise = [
    "Division de biens immobiliers résidentiels et commerciaux",
    "Sortie d'indivision amiable ou judiciaire",
    "Création et modification de copropriétés",
    "Accompagnement des marchands de biens",
    "Conseil en structuration juridique",
    "Optimisation fiscale des opérations"
  ];

  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="bg-[#1e3a5f] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <p className="text-[#c9a227] uppercase tracking-[0.2em] text-sm font-semibold mb-4">
              À Propos
            </p>
            <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
              Qui Sommes-Nous ?
            </h1>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              G Division est née de la volonté d'accompagner les propriétaires et investisseurs 
              dans leurs projets de division immobilière et de sortie d'indivision.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section with Video */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div>
              <p className="text-[#c9a227] uppercase tracking-[0.2em] text-sm font-semibold mb-4">
                Notre Histoire
              </p>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-6">
                Une Expertise Forgée par l'Expérience
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4 text-sm md:text-base">
                Fondée par des passionnés de l'immobilier, G Division s'est spécialisée dans 
                les opérations de division immobilière et l'accompagnement des situations 
                d'indivision complexes.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4 text-sm md:text-base">
                Notre équipe combine expertise juridique, connaissance du marché immobilier 
                et vision stratégique pour transformer des biens bloqués en véritables opportunités.
              </p>
              <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                Nous intervenons sur l'ensemble du territoire français, auprès de particuliers 
                comme de professionnels de l'immobilier.
              </p>
            </div>
            {/* Video Section */}
            <div className="order-first lg:order-last">
              <div className="aspect-video w-full rounded-sm overflow-hidden shadow-lg border-4 border-[#1e3a5f]/10">
                <iframe
                  src="https://www.youtube.com/embed/-2i2Yhe9HqU"
                  title="G Division - Présentation"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                  data-testid="about-video"
                ></iframe>
              </div>
              <div className="mt-4 text-center">
                <a 
                  href="https://www.youtube.com/@G10visions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-[#1e3a5f] hover:text-[#c9a227] transition-colors text-sm font-medium"
                >
                  <Play size={16} className="mr-2" />
                  Voir plus de vidéos sur notre chaîne
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-[#f8f9fa]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-[#c9a227] uppercase tracking-[0.2em] text-sm font-semibold mb-4">
              Nos Valeurs
            </p>
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-4">
              Ce Qui Nous Anime
            </h2>
            <div className="section-separator mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-white p-6 md:p-8 text-center card-hover shadow-sm"
                data-testid={`value-card-${index}`}
              >
                <div className="w-14 h-14 md:w-16 md:h-16 bg-[#1e3a5f] rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                  <value.icon className="text-[#c9a227]" size={24} />
                </div>
                <h3 className="font-['Playfair_Display'] text-lg md:text-xl font-semibold text-[#1e3a5f] mb-3 md:mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src="https://images.unsplash.com/photo-1581674662583-5e89b374fae6?w=800"
                alt="Expertise G Division"
                className="w-full h-[300px] md:h-[400px] object-cover shadow-lg"
              />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-[#c9a227] uppercase tracking-[0.2em] text-sm font-semibold mb-4">
                Nos Compétences
              </p>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-6 md:mb-8">
                Notre Expertise
              </h2>
              <ul className="space-y-3 md:space-y-4">
                {expertise.map((item, index) => (
                  <li key={index} className="flex items-start gap-3 md:gap-4">
                    <div className="w-5 h-5 md:w-6 md:h-6 bg-[#c9a227] rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="text-white" size={12} />
                    </div>
                    <span className="text-gray-700 text-sm md:text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA with Calendly */}
      <section className="py-16 md:py-24 bg-[#1e3a5f]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <Calendar className="text-[#c9a227] mx-auto mb-6" size={48} />
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-6">
            Réservez Votre Consultation Gratuite
          </h2>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-8 md:mb-10">
            Prenez rendez-vous directement avec notre équipe pour discuter de votre projet 
            de division ou d'indivision.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <CalendlyButton
              url="https://calendly.com/gdivision/rdv"
              text="Prendre Rendez-vous"
              className="bg-[#c9a227] text-white hover:bg-[#b08d22] rounded-none px-8 md:px-10 py-3 md:py-4 uppercase tracking-wider text-sm font-semibold transition-colors inline-flex items-center justify-center cursor-pointer"
              data-testid="calendly-btn"
            />
            <Link to="/contact">
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#1e3a5f] rounded-none px-8 md:px-10 py-3 md:py-4 uppercase tracking-wider text-sm font-semibold bg-transparent w-full sm:w-auto"
                data-testid="about-contact-btn"
              >
                Nous Écrire
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
